// src/components/holidays/index.js

import {
  useContext,
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import { Flex, Image } from "uikit-react";
import UIkit from "uikit";
import { useCookies } from "react-cookie";
import classnames from "classnames";
import { useLocation, useHistory } from "react-router-dom";

import { AppContexts } from "../../providers";

import PageTitle from "../common/PageTitle";
import Pagination from "../common/Pagination";
import RecordsPerPage from "../common/RecordsPerPage";
import TableLoading from "../common/Loading/table_loading";
import AddTextLine from "../common/AddTextLine";

import HolidayAdd from "./add";
import HolidayDelete from "./delete";

const sortOptions = [
  { key: "date", title: "Date" },
  { key: "name", title: "Name" },
  { key: "updated_at", title: "Last Updated" },
];

const defaultCriteria = {
  search_term: "",
  sort: "date",
  per: "20",
  page: 0,
  year: "", // optional filter
};

const parseQueryString = (queryString) => {
  return {
    ...defaultCriteria,
    ...Object.fromEntries(new URLSearchParams(queryString)),
  };
};

const Holidays = () => {
  const location = useLocation();
  const history = useHistory();
  const [criteria, setCriteria] = useState(() =>
    location.search ? parseQueryString(location.search) : defaultCriteria,
  );

  const holidaysContext = useContext(AppContexts.HolidaysContext);
  const settingsContext = useContext(AppContexts.SettingsContext); // if you want to store timezone or defaults
  const [cookies] = useCookies(["records-per-page"]);
  const { "records-per-page": recordsPerPage = 20 } = cookies;

  const { listHolidays, totalCnt, flagIsProcessing } = holidaysContext;

  const holidays = useMemo(
    () => (listHolidays?.length > recordsPerPage ? [] : listHolidays || []),
    [listHolidays, recordsPerPage],
  );

  useEffect(() => {
    document.title = "Holidays - RueBaRue";
    // settingsContext.get(["timezone"]); // optional
  }, []);

  useEffect(() => {
    setCriteria((it) => ({ ...it, per: recordsPerPage }));
  }, [recordsPerPage]);

  useEffect(() => {
    let timeoutId = setTimeout(
      () => holidaysContext.searchHolidays(criteria),
      300,
    );
    return () => clearTimeout(timeoutId);
  }, [JSON.stringify(criteria)]);

  const updateQueryParams = (newCriteria) => {
    const searchParams = new URLSearchParams(newCriteria).toString();
    history.replace(`/holidays?${searchParams}`);
  };

  const handleCriteriaChange = (key, value) => {
    const newCriteria = { ...criteria, [key]: value, page: 0 };
    setCriteria(newCriteria);
    updateQueryParams(newCriteria);
  };

  const handleIndexChanged = useCallback((newPageIndex) => {
    const newCriteria = { ...criteria, page: newPageIndex };
    setCriteria(newCriteria);
    updateQueryParams(newCriteria);
  }, [criteria]);

  const handleAdd = useCallback(() => {
    holidaysContext.setIsNew(true);
    holidaysContext.setHoliday({});
  }, []);

  const handleEdit = useCallback((holiday) => {
    holidaysContext.setIsNew(false);
    holidaysContext.setHoliday(holiday);
  }, []);

  const handleDelete = useCallback((holiday) => {
    holidaysContext.setHoliday(holiday);
  }, []);

  const handleQuickToggleObserved = useCallback(
    async (holiday) => {
      try {
        const updated = { ...holiday, observed: !holiday.observed };
        const ok = await holidaysContext.updateHoliday(updated);
        if (ok) UIkit.notification("Holiday updated", "success");
        else UIkit.notification("Failed", "error");
      } catch (e) {
        UIkit.notification("Failed", "error");
      }
    },
    [holidaysContext],
  );

  const holidayRenderer = useCallback(
    (holiday) => (
      <tr key={holiday?.id}>
        <td className="uk-table-expand">
          <div className="semi-bold-font">{holiday?.name}</div>
          <div className="secondary">
            {holiday?.scope === "company"
              ? "Company-wide"
              : "Selected staff/groups"}
            {holiday?.is_recurring ? " • Recurs yearly" : ""}
          </div>
        </td>

        <td className="uk-table-shrink">
          <div className="semi-bold-font">{holiday?.date_display}</div>
          <div className="secondary">{holiday?.year ? `Year: ${holiday.year}` : ""}</div>
        </td>

        <td className="uk-table-shrink">
          <label className="uk-switch uk-display-inline-flex uk-flex-middle">
            <input
              type="checkbox"
              checked={!!holiday?.observed}
              onChange={() => handleQuickToggleObserved(holiday)}
            />
            <span className="uk-switch-slider" />
          </label>
        </td>

        <td className="uk-table-shrink">
          <div className={classnames("hms-booking-tag", {
            "uk-label uk-label-success": holiday?.type === "national",
            "uk-label uk-label-warning": holiday?.type === "custom",
          })}>
            {holiday?.type === "national" ? "national" : "custom"}
          </div>
        </td>

        <td className="hms-action-btn uk-width-205">
          <div className="uk-flex uk-flex-top">
            <div className="uk-inline">
              <button
                className="uk-button"
                uk-toggle="target: #modal-holiday-add"
                onClick={() => handleEdit(holiday)}
                uk-tooltip="Edit"
              >
                <Image src="/images/edit.svg" />
              </button>
            </div>
            <div className="uk-inline">
              <button
                className="uk-button"
                uk-toggle="target: #modal-holiday-delete"
                onClick={() => handleDelete(holiday)}
                uk-tooltip="Delete"
              >
                <Image src="/images/delete.svg" />
              </button>
            </div>
          </div>
        </td>
      </tr>
    ),
    [handleEdit, handleDelete, handleQuickToggleObserved],
  );

  return (
    <>
      <section id="view-holidays">
        <AddTextLine feature="Holidays" />
        <PageTitle title="Holidays" />

        <div id="hms-main-body">
          <div className="uk-flex uk-flex-between uk-flex-top">
            <div className="hms-guest-btn">
              <button
                className="uk-button hms-btn hms-red-btn"
                uk-toggle="target: #modal-holiday-add"
                onClick={handleAdd}
              >
                <span uk-icon="icon: plus; ratio: 0.7"></span> Holiday
              </button>

              {/* Optional: preload national holidays button */}
              <button
                className="uk-button hms-btn hms-white-btn"
                onClick={() => UIkit.notification("Hook this to a preload endpoint", "primary")}
              >
                <span uk-icon="icon: refresh; ratio: 0.9"></span> Load National Holidays
              </button>
            </div>
          </div>

          <div className="uk-card uk-card-small uk-card-default hms-form-card">
            <div className="uk-overflow-auto">
              <form className="uk-form">
                <Flex alignment="between top" className="guest-form-top-filters">
                  <div className="hms-form">
                    <div className="uk-inline">
                      <div uk-form-custom="target: > * > span:first-child">
                        <select
                          value={criteria.year}
                          onChange={(e) => handleCriteriaChange("year", e.target.value)}
                        >
                          <option value="">All Years</option>
                          {Array.from({ length: 4 }).map((_, idx) => {
                            const y = new Date().getFullYear() + idx;
                            return (
                              <option value={String(y)} key={`year_${y}`}>
                                {y}
                              </option>
                            );
                          })}
                        </select>
                        <button className="uk-button uk-button-default" type="button" tabIndex="-1">
                          <span></span>
                          <span uk-icon="icon: chevron-down"></span>
                        </button>
                      </div>
                    </div>

                    <div className="uk-inline">
                      <div className="uk-search uk-search-default">
                        {criteria.search_term ? (
                          <a
                            className="uk-form-icon-flip uk-form-icon"
                            uk-icon="icon: close"
                            onClick={() => handleCriteriaChange("search_term", "")}
                          ></a>
                        ) : (
                          <span className="uk-search-icon-flip" uk-search-icon="true" />
                        )}
                        <input
                          className="uk-search-input"
                          type="search"
                          placeholder="Holiday name"
                          value={criteria.search_term}
                          onChange={(e) => handleCriteriaChange("search_term", e.target.value)}
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="hms-sortby-btn">
                      <button className="uk-button" type="button">
                        SORT BY<span uk-icon="icon: triangle-down"></span>
                      </button>
                      <div uk-dropdown="mode: click; offset: 0; animation: uk-animation-slide-top-small; duration: 400">
                        <ul className="uk-nav uk-dropdown-nav">
                          {sortOptions.map((option, idx) => (
                            <li
                              className={classnames({ "uk-active": criteria.sort === option.key })}
                              key={`sort_option_${idx}`}
                            >
                              <a
                                href="#"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleCriteriaChange("sort", option.key);
                                }}
                              >
                                {option.title}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </Flex>
              </form>

              <table className="uk-table uk-table-hover uk-table-divider hms-table">
                <thead>
                  <tr>
                    <th className="uk-table-expand">Holiday</th>
                    <th className="uk-table-shrink">Date</th>
                    <th className="uk-table-shrink">Observed</th>
                    <th className="uk-table-shrink">Type</th>
                    <th className="uk-width-205 actions">ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {flagIsProcessing ? (
                    TableLoading(5)
                  ) : holidays.length > 0 ? (
                    holidays.map(holidayRenderer)
                  ) : (
                    <tr>
                      <td colSpan={9} style={{ padding: "30px 25px" }}>
                        No holidays matching your current search.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <Flex alignment="between top" className="guest-form-top-filters">
            <Pagination
              count={totalCnt}
              index={Number(criteria.page)}
              onIndexChanged={handleIndexChanged}
            />
            {totalCnt > 0 && <RecordsPerPage />}
          </Flex>
        </div>
      </section>

      <HolidayAdd />
      <HolidayDelete />
    </>
  );
};

export default Holidays;